import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Snackbar,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  listPersons,
  deletePerson as deletePersonAPI,
  listAssociatedPersons,
  deleteAssociatedPerson as deleteAssociatedPersonAPI
} from '../services/api/personManagementService';
import PersonList from './PersonList';
import PersonDialog from './PersonDialog';
import AssociatedPersonList from './AssociatedPersonList';
import AssociatedPersonDialog from './AssociatedPersonDialog';

/**
 * PersonManagement - Interface for managing Person and AssociatedPerson records
 *
 * Features:
 * - List/search persons with filtering
 * - Create/edit/delete persons
 * - Manage associated persons (unknowns)
 * - Promote associated persons to known persons
 * - Rekognition collection synchronization
 */
export default function PersonManagement() {
  // Tab state (0 = Persons, 1 = Associated Persons)
  const [activeTab, setActiveTab] = useState(0);

  // Persons state
  const [persons, setPersons] = useState([]);
  const [personsLoading, setPersonsLoading] = useState(true);
  const [personsError, setPersonsError] = useState(null);

  // Associated persons state
  const [associatedPersons, setAssociatedPersons] = useState([]);
  const [associatedLoading, setAssociatedLoading] = useState(false);
  const [associatedError, setAssociatedError] = useState(null);

  // Dialog state
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [selectedAssociatedPerson, setSelectedAssociatedPerson] = useState(null);
  const [associatedDialogOpen, setAssociatedDialogOpen] = useState(false);
  const [associatedDeleteDialogOpen, setAssociatedDeleteDialogOpen] = useState(false);
  const [associatedDeleteConfirmText, setAssociatedDeleteConfirmText] = useState('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Load persons on mount
  useEffect(() => {
    loadPersons();
  }, []);

  // Load associated persons when tab switches
  useEffect(() => {
    if (activeTab === 1 && associatedPersons.length === 0) {
      loadAssociatedPersons();
    }
  }, [activeTab]);

  async function loadPersons() {
    try {
      setPersonsLoading(true);
      setPersonsError(null);
      const data = await listPersons();

      // Normalize dataset field: if missing, use datasets[0] for backward compatibility
      const normalizedData = data.map(person => ({
        ...person,
        dataset: person.dataset || (person.datasets && person.datasets[0]) || ''
      }));

      setPersons(normalizedData);
    } catch (err) {
      console.error('Failed to load persons:', err);
      setPersonsError(err.message);
    } finally {
      setPersonsLoading(false);
    }
  }

  async function loadAssociatedPersons() {
    try {
      setAssociatedLoading(true);
      setAssociatedError(null);
      const data = await listAssociatedPersons();
      setAssociatedPersons(data);
    } catch (err) {
      console.error('Failed to load associated persons:', err);
      setAssociatedError(err.message);
    } finally {
      setAssociatedLoading(false);
    }
  }

  function handleTabChange(event, newValue) {
    setActiveTab(newValue);
  }

  // Person handlers
  function handlePersonClick(person) {
    setSelectedPerson(person);
    setPersonDialogOpen(true);
  }

  function handleCreateClick() {
    setSelectedPerson(null);
    setPersonDialogOpen(true);
  }

  function handlePersonSaved(personData) {
    const isEdit = !!selectedPerson;
    showSnackbar(
      isEdit
        ? 'Person updated successfully'
        : `Person "${personData.first_name} ${personData.last_name}" created successfully`,
      'success'
    );
    setPersonDialogOpen(false);
    setSelectedPerson(null);

    if (isEdit) {
      // Update existing person in state without re-fetching all
      setPersons(prevPersons =>
        prevPersons.map(p =>
          p.person_id === selectedPerson.person_id
            ? { ...p, ...personData }
            : p
        )
      );
    } else {
      // For new person, reload to get server-generated data
      loadPersons();
    }
  }

  function handleDeleteClick(person) {
    setSelectedPerson(person);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  }

  async function handleDeleteConfirm() {
    if (!selectedPerson) return;
    if (deleteConfirmText !== selectedPerson.person_id) {
      showSnackbar('Person ID does not match', 'error');
      return;
    }

    try {
      await deletePersonAPI(selectedPerson.person_id);
      showSnackbar(
        `Person "${selectedPerson.first_name} ${selectedPerson.last_name}" deleted`,
        'success'
      );
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      setSelectedPerson(null);
      loadPersons();
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete person', 'error');
    }
  }

  // Associated person handlers
  function handleAssociatedPersonClick(associatedPerson) {
    setSelectedAssociatedPerson(associatedPerson);
    setAssociatedDialogOpen(true);
  }

  function handleAssociatedPersonUpdated() {
    showSnackbar('Associated person updated successfully', 'success');
    loadAssociatedPersons();
  }

  function handleAssociatedPersonPromoted(newPerson) {
    showSnackbar(
      `Associated person promoted to "${newPerson.first_name} ${newPerson.last_name}"`,
      'success'
    );
    setAssociatedDialogOpen(false);
    setSelectedAssociatedPerson(null);
    loadAssociatedPersons();
    loadPersons(); // Refresh persons list to show new person
  }

  function handleAssociatedDeleteClick(associatedPerson) {
    setSelectedAssociatedPerson(associatedPerson);
    setAssociatedDeleteConfirmText('');
    setAssociatedDeleteDialogOpen(true);
  }

  async function handleAssociatedDeleteConfirm() {
    if (!selectedAssociatedPerson) return;
    if (associatedDeleteConfirmText !== selectedAssociatedPerson.person_id) {
      showSnackbar('Person ID does not match', 'error');
      return;
    }

    try {
      await deleteAssociatedPersonAPI(selectedAssociatedPerson.person_id);
      showSnackbar('Associated person deleted', 'success');
      setAssociatedDeleteDialogOpen(false);
      setAssociatedDeleteConfirmText('');
      setSelectedAssociatedPerson(null);
      loadAssociatedPersons();
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete associated person', 'error');
    }
  }

  function showSnackbar(message, severity = 'info') {
    setSnackbar({ open: true, message, severity });
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Person Management
        </Typography>
        {activeTab === 0 && (
          <IconButton
            color="primary"
            onClick={handleCreateClick}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              borderRadius: 1
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Persons" />
        <Tab label="Associated Persons" />
      </Tabs>

      {/* Persons Tab */}
      {activeTab === 0 && (
        <>
          {/* Error Message */}
          {personsError && (
            <Alert
              severity="error"
              variant="outlined"
              onClose={() => setPersonsError(null)}
            >
              {personsError}
            </Alert>
          )}

          {/* Loading State */}
          {personsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          )}

          {/* Empty State */}
          {!personsLoading && persons.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No persons
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first person to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
              >
                Create Person
              </Button>
            </Box>
          )}

          {/* Person List */}
          {!personsLoading && persons.length > 0 && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <PersonList
                persons={persons}
                onPersonClick={handlePersonClick}
                onDelete={handleDeleteClick}
              />
            </Box>
          )}
        </>
      )}

      {/* Associated Persons Tab */}
      {activeTab === 1 && (
        <>
          {/* Error Message */}
          {associatedError && (
            <Alert
              severity="error"
              variant="outlined"
              onClose={() => setAssociatedError(null)}
            >
              {associatedError}
            </Alert>
          )}

          {/* Loading State */}
          {associatedLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          )}

          {/* Empty State */}
          {!associatedLoading && associatedPersons.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 10,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No associated persons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Associated persons will appear here after face matching detects unknown faces
              </Typography>
            </Box>
          )}

          {/* Associated Person List */}
          {!associatedLoading && associatedPersons.length > 0 && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <AssociatedPersonList
                associatedPersons={associatedPersons}
                onPersonClick={handleAssociatedPersonClick}
                onDelete={handleAssociatedDeleteClick}
              />
            </Box>
          )}
        </>
      )}

      {/* Person Dialog (Create/Edit) */}
      <PersonDialog
        open={personDialogOpen}
        person={selectedPerson}
        onClose={() => {
          setPersonDialogOpen(false);
          setSelectedPerson(null);
        }}
        onSaved={handlePersonSaved}
      />

      {/* Delete Person Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Person</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Delete <strong>{selectedPerson?.first_name} {selectedPerson?.last_name}</strong> (ID: {selectedPerson?.person_id})?
            <br /><br />
            This will permanently remove the person record and all indexed face images from Rekognition.
            This action cannot be undone.
            <br /><br />
            To confirm, type the person ID: <strong>{selectedPerson?.person_id}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={selectedPerson?.person_id}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== selectedPerson?.person_id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Associated Person Dialog */}
      <AssociatedPersonDialog
        open={associatedDialogOpen}
        associatedPerson={selectedAssociatedPerson}
        onClose={() => {
          setAssociatedDialogOpen(false);
          setSelectedAssociatedPerson(null);
        }}
        onUpdated={handleAssociatedPersonUpdated}
        onPromoted={handleAssociatedPersonPromoted}
      />

      {/* Delete Associated Person Confirmation Dialog */}
      <Dialog
        open={associatedDeleteDialogOpen}
        onClose={() => setAssociatedDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Associated Person</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Delete associated person <strong>{selectedAssociatedPerson?.person_id}</strong>?
            <br /><br />
            This will permanently remove the associated person record and face image from Rekognition.
            This action cannot be undone.
            <br /><br />
            To confirm, type the person ID: <strong>{selectedAssociatedPerson?.person_id}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={selectedAssociatedPerson?.person_id}
            value={associatedDeleteConfirmText}
            onChange={(e) => setAssociatedDeleteConfirmText(e.target.value)}
            sx={{ fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssociatedDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssociatedDeleteConfirm}
            color="error"
            variant="contained"
            disabled={associatedDeleteConfirmText !== selectedAssociatedPerson?.person_id}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
